import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { shippingApi } from './api';
import VisualReviewPanel from './VisualReviewPanel';

const purposes = { PACKING_CONTENTS: 'Contents beside addressed package', PACKAGE_FINAL: 'Additional package detail', ITEM_DETAIL: 'Additional item detail', OTHER: 'Other packing evidence' };
function requestKey() {
  if (!window.crypto?.getRandomValues) throw new Error('Secure capture IDs require a supported browser over HTTPS.');
  return Array.from(window.crypto.getRandomValues(new Uint8Array(16)), n => n.toString(16).padStart(2, '0')).join('');
}
function usePrivateUrl(blob) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!blob) { setUrl(''); return undefined; }
    const value = URL.createObjectURL(blob); setUrl(value);
    return () => URL.revokeObjectURL(value);
  }, [blob]);
  return url;
}

export default function PackingPhotoCapture({ job, revisions, csrfToken, disabled, packageVerificationRequired = true, packageVerified, onAccessLost, onBusy, onReady }) {
  const [history, setHistory] = useState(null);
  const [capture, setCapture] = useState(null);
  const [purpose, setPurpose] = useState('PACKING_CONTENTS');
  const [file, setFile] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [reviewBeforeSave, setReviewBeforeSave] = useState(false);
  const [buyerCopy, setBuyerCopy] = useState(false);
  const [buyerApproved, setBuyerApproved] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0, width: 50, height: 100 });
  const [size, setSize] = useState(null);
  const [savedBlob, setSavedBlob] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [visualPending, setVisualPending] = useState(() => new Set());
  const onVisualBusy = useCallback((key, active) => setVisualPending(previous => {
    if (previous.has(key) === active) return previous;
    const next = new Set(previous); if (active) next.add(key); else next.delete(key); return next;
  }), []);
  const [retry, setRetry] = useState(false);
  const pending = useRef(null);
  const flight = useRef(false);
  const autoBeginKey = useRef('');
  const autoPreviewKey = useRef('');
  const controller = useRef(null);
  const videoRef = useRef(null);
  const cameraStream = useRef(null);
  const fileUrl = usePrivateUrl(file);
  const savedUrl = usePrivateUrl(savedBlob);
  const fail = useCallback(err => {
    if (err.name === 'AbortError' || controller.current?.signal.aborted) return;
    if (err.status === 401 || err.status === 403) onAccessLost(err);
    else setError(err.message || 'Photo request failed. Your package has not advanced.');
  }, [onAccessLost]);
  const refresh = useCallback(async () => {
    const signal = controller.current.signal;
    const result = await shippingApi.evidence(job.id, signal);
    if (!signal.aborted) setHistory(result);
  }, [job.id]);
  useEffect(() => {
    const current = new AbortController(); controller.current = current;
    refresh().catch(fail);
    return () => {
      current.abort();
      cameraStream.current?.getTracks?.().forEach(track => track.stop());
      cameraStream.current = null;
      onBusy(false); onReady(false);
    };
  }, [refresh, fail, onBusy, onReady]);
  useEffect(() => { onBusy(busy || cameraStarting || Boolean(capture) || retry || visualPending.size > 0); }, [busy, cameraStarting, capture, retry, visualPending, onBusy]);
  useEffect(() => { onReady(history?.ready_for_seal === true && !capture && !retry && !busy); }, [history, capture, retry, busy, onReady]);
  useEffect(() => {
    const current = history?.evidence?.find(row => row.status === 'CURRENT' && row.purpose === 'PACKING_CONTENTS');
    const key = current?.evidence_key;
    if (!key || savedBlob || autoPreviewKey.current === key || !controller.current) return undefined;
    autoPreviewKey.current = key;
    shippingApi.evidenceFile(key, 'display', controller.current.signal)
      .then(blob => { if (!controller.current?.signal.aborted) setSavedBlob(blob); })
      .catch(() => { if (!controller.current?.signal.aborted) autoPreviewKey.current = ''; });
    return undefined;
  }, [history, savedBlob]);

  useEffect(() => {
    if (!cameraOpen || !videoRef.current || !cameraStream.current) return undefined;
    const video = videoRef.current;
    const stream = cameraStream.current;
    video.srcObject = stream;
    const started = video.play?.();
    if (started?.catch) started.catch(() => {});
    return () => { if (video.srcObject === stream) video.srcObject = null; };
  }, [cameraOpen]);

  useEffect(() => {
    const binding = `${job.id}:${revisions.job_revision}:${revisions.shipment_revision}:${revisions.manifest_revision}:${revisions.address_revision}:${revisions.ownership_epoch}`;
    if (!packageVerified || !history || history.ready_for_seal || capture || retry || busy || disabled || history.pending_captures?.length) return;
    if (autoBeginKey.current === binding) return;
    autoBeginKey.current = binding;
    begin();
  // begin intentionally uses the current immutable binding; reruns are keyed above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id, revisions.job_revision, revisions.shipment_revision, revisions.manifest_revision, revisions.address_revision, revisions.ownership_epoch,
    packageVerified, history, capture, retry, busy, disabled]);

  function stopCamera() {
    cameraStream.current?.getTracks?.().forEach(track => track.stop());
    cameraStream.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOpen(false); setCameraReady(false); setCameraStarting(false);
  }
  function clearFile() { setFile(null); setSize(null); setConfirmed(false); setBuyerCopy(false); setBuyerApproved(false); }
  async function execute(command) {
    if (flight.current) return;
    flight.current = true; setBusy(true); setError(''); setNotice('');
    pending.current = command; setRetry(false);
    const signal = controller.current.signal;
    try {
      let result;
      if (command.kind === 'upload') {
        // Reconcile the original capture before retrying uncertain transport. Never query the next active job.
        const status = await shippingApi.capture(command.captureKey, signal);
        result = status.evidence ? status : await shippingApi.uploadEvidence(command.captureKey, command.file, command.payload, csrfToken, signal);
      } else result = await shippingApi.packingCommand(command.path, command.payload, csrfToken, signal);
      if (signal.aborted) return;
      pending.current = null;
      if (command.kind === 'begin' && result.capture.status === 'OPEN' && result.capture.current_binding) {
        clearFile(); setCapture(result.capture);
      } else {
        setCapture(null); clearFile();
        const currentEvidence = result.evidence?.status === 'CURRENT' ? result.evidence : null;
        setNotice(currentEvidence ? 'Photo accepted for this package.' : result.evidence ? 'Photo retained against its original capture. It does not satisfy this package’s current photo check.' : 'Capture closed.');
        if (command.kind === 'upload' && currentEvidence) {
          // Show the exact saved server copy immediately. If that read-back fails, keep the
          // successful upload state and fall back to the photo bytes the operator just sent.
          try {
            const persisted = await shippingApi.evidenceFile(currentEvidence.evidence_key, 'display', signal);
            if (!signal.aborted) setSavedBlob(persisted);
          } catch (previewErr) {
            if (!signal.aborted && command.file) setSavedBlob(command.file);
          }
        }
      }
      await refresh();
    } catch (err) {
      if (signal.aborted) return;
      // Unknown outcomes retain the exact request, bytes and approval. A retry cannot silently become a retake.
      if (pending.current && (!err.status || err.status >= 500)) setRetry(true);
      else pending.current = null;
      fail(err);
    } finally { flight.current = false; if (!signal.aborted) setBusy(false); }
  }
  function begin(retake = null) {
    try {
      execute({ kind: 'begin', path: `jobs/${job.id}/captures`, payload: {
        request_key: requestKey(), expected: revisions, purpose: retake?.purpose || purpose, retake_of: retake?.evidence_key || null,
      } });
    } catch (err) { fail(err); }
  }
  function cancel(key) {
    stopCamera();
    try { execute({ kind: 'cancel', path: `captures/${key}/cancel`, payload: { request_key: requestKey() } }); }
    catch (err) { fail(err); }
  }
  async function resume(key) {
    if (flight.current) return;
    flight.current = true; setBusy(true); setError('');
    try {
      const result = await shippingApi.capture(key, controller.current.signal);
      if (controller.current.signal.aborted) return;
      if (result.capture.current_binding && result.capture.status === 'OPEN') { clearFile(); setCapture(result.capture); }
      else { setError('This capture is no longer current. Cancel it and begin a fresh capture after rechecking the package.'); await refresh(); }
    } catch (err) { fail(err); }
    finally { flight.current = false; if (!controller.current.signal.aborted) setBusy(false); }
  }
  async function view(row, variant) {
    if (flight.current) return;
    flight.current = true; setBusy(true); setError(''); setSavedBlob(null);
    try {
      const blob = await shippingApi.evidenceFile(row.evidence_key, variant, controller.current.signal);
      if (!controller.current.signal.aborted) setSavedBlob(blob);
    } catch (err) { fail(err); }
    finally { flight.current = false; if (!controller.current.signal.aborted) setBusy(false); }
  }
  const cropValid = Object.values(crop).every(v => Number.isFinite(v) && v >= 0 && v <= 100) && crop.width > 0 && crop.height > 0 && crop.x + crop.width <= 100 && crop.y + crop.height <= 100;
  function upload() {
    try { execute({ kind: 'upload', captureKey: capture.capture_key, file, payload: {
      request_key: requestKey(), photo_confirmed: confirmed, buyer_approved: buyerCopy ? buyerApproved : true,
      crop: buyerCopy ? Object.fromEntries(Object.entries(crop).map(([k, v]) => [k, v / 100])) : null,
    } }); } catch (err) { fail(err); }
  }
  function acceptSelectedPhoto(selected) {
    clearFile(); setError('');
    if (!selected) return;
    if (selected.size > 8 * 1024 * 1024 || !['image/jpeg', 'image/png', 'image/webp'].includes(selected.type)) {
      setError('Choose a JPEG, PNG or WebP photo up to 8 MiB. Convert HEIC first.'); return;
    }
    if (reviewBeforeSave) { setFile(selected); return; }
    try { execute({ kind: 'upload', captureKey: capture.capture_key, file: selected, payload: {
      request_key: requestKey(), photo_confirmation_basis: 'OPERATOR_CAPTURED_OR_SELECTED_CURRENT_PACKAGE', buyer_approved: true, crop: null,
    } }); } catch (err) { fail(err); }
  }
  function choose(event) {
    const selected = event.target.files?.[0]; event.target.value = '';
    acceptSelectedPhoto(selected);
  }
  async function openCamera() {
    if (cameraStarting || cameraOpen || blocked || !capture) return;
    setError(''); setNotice(''); setCameraStarting(true); setCameraReady(false);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraStarting(false);
      setError('Live camera capture is not available in this browser. Use Choose existing photo instead.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      if (controller.current?.signal.aborted) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }
      cameraStream.current = stream;
      setCameraOpen(true);
    } catch (err) {
      setError(err?.name === 'NotAllowedError'
        ? 'Camera permission was not granted. Allow camera access or use Choose existing photo.'
        : 'The live camera could not be opened. Use Choose existing photo instead.');
    } finally {
      if (!controller.current?.signal.aborted) setCameraStarting(false);
    }
  }
  function takeCameraPhoto() {
    const video = videoRef.current;
    if (!video || !cameraReady || !video.videoWidth || !video.videoHeight) {
      setError('The camera is not ready yet. Wait for the preview, then take the photo.');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) { setError('This browser could not capture the camera frame. Use Choose existing photo instead.'); return; }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(blob => {
      if (!blob) { setError('The camera photo could not be created. Use Choose existing photo instead.'); return; }
      const selected = new File([blob], `packing-photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      stopCamera();
      acceptSelectedPhoto(selected);
    }, 'image/jpeg', 0.92);
  }
  const blocked = disabled || busy || retry;
  return <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, bgcolor: 'background.paper' }}><Stack spacing={1.5}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>Take packing photo</Typography>
        <Typography variant="body2" color="text.secondary">Photograph the verified contents beside the addressed package before final sealing.</Typography>
      </Box>
      {history?.ready_for_seal && <Typography variant="caption" sx={{ px: 1, py: 0.5, borderRadius: 99, bgcolor: 'rgba(46,125,50,0.10)', color: 'success.dark', fontWeight: 800 }}>PHOTO SAVED</Typography>}
    </Stack>

    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 0.75 }}>
      {['Place verified contents beside the addressed package', 'Take one clear packing photo', 'After it saves, insert and seal'].map((label, index) => <Stack key={label} direction="row" gap={0.75} alignItems="flex-start" sx={{ p: 1, borderRadius: 2, bgcolor: 'grey.50' }}>
        <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 800, flexShrink: 0 }}>{index + 1}</Box>
        <Typography variant="caption">{label}</Typography>
      </Stack>)}
    </Box>

    {error && <Alert severity="error">{error}</Alert>}
    {notice && <Alert severity="info">{notice}</Alert>}
    {retry && <Button disabled={busy} onClick={() => execute(pending.current)}>Retry the same photo request</Button>}
    {packageVerificationRequired && !packageVerified && <Alert severity="info">Scan this package’s current label before starting capture.</Alert>}
    {!capture && !retry && !history?.ready_for_seal && <>
      {packageVerificationRequired && packageVerified && <Alert severity="info" sx={{ py: 0.25 }}>Package verified. Preparing the photo task…</Alert>}
      <Button size="large" variant="outlined" disabled={blocked || !packageVerified} onClick={() => begin()}>Open photo capture</Button>
    </>}
    {capture && <>
      {!cameraOpen && <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
        <Button size="large" variant="contained" sx={{ flex: 1, py: 1.2 }} disabled={blocked || cameraStarting} onClick={openCamera}>{cameraStarting ? 'OPENING CAMERA…' : 'TAKE PACKING PHOTO'}</Button>
        <Button component="label" size="large" variant="outlined" sx={{ flex: 1 }} disabled={blocked || cameraStarting}>Choose existing photo<input aria-label="Choose packing photo" hidden type="file" accept="image/jpeg,image/png,image/webp" disabled={blocked || cameraStarting} onChange={choose} /></Button>
        <Button disabled={busy || retry || cameraStarting} onClick={() => cancel(capture.capture_key)}>Cancel capture</Button>
      </Stack>}
      {cameraOpen && <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
        <Stack spacing={1}>
          <Typography sx={{ fontWeight: 750 }}>Camera preview</Typography>
          <Box component="video" ref={videoRef} aria-label="Live packing camera preview" autoPlay muted playsInline onLoadedMetadata={() => setCameraReady(true)} sx={{ width: '100%', maxHeight: '55vh', bgcolor: 'common.black', borderRadius: 2, objectFit: 'contain' }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
            <Button size="large" variant="contained" sx={{ flex: 1 }} disabled={blocked || !cameraReady} onClick={takeCameraPhoto}>TAKE PHOTO NOW</Button>
            <Button size="large" variant="outlined" disabled={busy || retry} onClick={stopCamera}>CANCEL CAMERA</Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">The camera stays inside Packing Station so the active capture remains mounted. The photo uploads immediately after you take it.</Typography>
        </Stack>
      </Paper>}
      <Typography variant="caption" color="text.secondary">The normal path saves this full packing photo for the package and approves the metadata-free copy for possible later sending to this buyer. Nothing is sent at capture time.</Typography>
      <Box component="details">
        <Typography component="summary" variant="body2" sx={{ cursor: 'pointer', fontWeight: 650, color: 'text.secondary', userSelect: 'none' }}>Advanced photo review / optional buyer crop</Typography>
        <Stack spacing={1} sx={{ pt: 1 }}>
          <Typography variant="caption" color="text.secondary">Capture binding · Job {capture.job_id} · Manifest {capture.manifest_revision} · Address {capture.address_revision} · {purposes[capture.purpose]}</Typography>
          <FormControlLabel control={<Checkbox disabled={blocked || Boolean(file)} checked={reviewBeforeSave} onChange={e => setReviewBeforeSave(e.target.checked)} />} label="Review the photo before saving (required only if preparing an optional crop)" />
          <TextField select label="Photo purpose" value={purpose} disabled={blocked || capture.purpose === 'PACKING_CONTENTS'} onChange={e => setPurpose(e.target.value)}>
            {Object.entries(purposes).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
          </TextField>
        </Stack>
      </Box>
      {fileUrl && <>
        <Box component="img" src={fileUrl} alt="Unsaved private packing evidence" onLoad={e => setSize({ width: e.target.naturalWidth, height: e.target.naturalHeight })} onError={() => setError('The browser could not preview this image. Choose a supported photo.')} sx={{ maxWidth: '100%', maxHeight: 480, objectFit: 'contain', borderRadius: 2 }} />
        <FormControlLabel control={<Checkbox disabled={blocked} checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />} label="I reviewed this photo: it clearly shows the stated subject and the correct addressed package before final sealing" />
        <FormControlLabel control={<Checkbox disabled={blocked} checked={buyerCopy} onChange={e => { setBuyerCopy(e.target.checked); setBuyerApproved(false); }} />} label="Use a cropped photo instead of the full packing photo for the later buyer message" />
        {buyerCopy && <>
          <Alert severity="warning">Crop only if you prefer not to send the full packing photo. The normal full photo may include this buyer’s addressed envelope. Nothing is sent at capture time.</Alert>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>{Object.entries(crop).map(([field, value]) => <TextField key={field} label={`Crop ${field} (%)`} type="number" value={value} disabled={blocked} inputProps={{ min: 0, max: 100 }} onChange={e => { setCrop(c => ({ ...c, [field]: Number(e.target.value) })); setBuyerApproved(false); }} />)}</Stack>
          {cropValid && size && <Box component="svg" role="img" aria-label="Proposed buyer-safe crop" viewBox={`${crop.x * size.width / 100} ${crop.y * size.height / 100} ${crop.width * size.width / 100} ${crop.height * size.height / 100}`} sx={{ width: '100%', maxHeight: 300, overflow: 'hidden' }}>
            <image href={fileUrl} width={size.width} height={size.height} />
          </Box>}
          <FormControlLabel control={<Checkbox disabled={blocked || !cropValid || !size} checked={buyerApproved} onChange={e => setBuyerApproved(e.target.checked)} />} label="I reviewed this crop and approve it for later sending to this buyer" />
        </>}
        <Button size="large" fullWidth variant="contained" disabled={blocked || !confirmed || !size || (buyerCopy && (!buyerApproved || !cropValid))} onClick={upload}>Save reviewed photo for this package</Button>
      </>}
    </>}
    {history?.pending_captures.filter(c => c.capture_key !== capture?.capture_key).map(c => <Stack key={c.capture_key} direction={{ xs: 'column', sm: 'row' }} gap={1}>
      <Typography>Unfinished capture · {purposes[c.purpose]}</Typography>
      <Button disabled={blocked || Boolean(capture)} onClick={() => resume(c.capture_key)}>Resume capture</Button>
      <Button disabled={busy || retry || Boolean(capture)} onClick={() => cancel(c.capture_key)}>Cancel unfinished capture</Button>
    </Stack>)}
    {savedUrl && <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, borderColor: 'success.light', bgcolor: 'rgba(46,125,50,0.04)' }}>
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Box>
            <Typography sx={{ fontWeight: 800 }}>Saved packing photo</Typography>
            <Typography variant="caption" color="text.secondary">This is the photo saved against the current package.</Typography>
          </Box>
          <Typography variant="caption" sx={{ px: 1, py: 0.5, borderRadius: 99, bgcolor: 'rgba(46,125,50,0.10)', color: 'success.dark', fontWeight: 800 }}>SAVED</Typography>
        </Stack>
        <Box component="img" src={savedUrl} alt="Saved packing photo for current package" sx={{ width: '100%', maxHeight: 480, objectFit: 'contain', borderRadius: 2, bgcolor: 'grey.50' }} />
        <Typography variant="caption" color="text.secondary">Confirm this is the intended contents + addressed-package photo before sealing. Retake controls remain under Saved photo history and review tools.</Typography>
      </Stack>
    </Paper>}
    {history?.evidence?.length > 0 && <Box component="details"><Typography component="summary" variant="body2" sx={{ cursor: 'pointer', fontWeight: 650, color: 'text.secondary', userSelect: 'none' }}>Saved photo history and review tools</Typography><Stack spacing={1} sx={{ pt: 1 }}>{history.evidence.map(row => <Paper variant="outlined" key={row.evidence_key} sx={{ p: 1.25, borderRadius: 2 }}>
      <Typography>{purposes[row.purpose]} · {row.status} · Manifest {row.manifest_revision} / Address {row.address_revision}</Typography>
      <Typography variant="caption">{row.buyer_copy_available ? 'Buyer-ready photo retained privately' : 'Buyer copy withheld'}{row.quarantine_codes?.length ? ` · ${row.quarantine_codes.join(', ')}` : ''}</Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        <Button disabled={busy || retry} onClick={() => view(row, 'display')}>Review private photo</Button>
        {row.buyer_copy_available && <Button disabled={busy || retry} onClick={() => view(row, 'buyer')}>Review buyer copy</Button>}
        {row.status === 'CURRENT' && <Button disabled={blocked || Boolean(capture) || (packageVerificationRequired && !packageVerified)} onClick={() => begin(row)}>Retake · retain original</Button>}
      </Stack>
      {row.status === 'CURRENT' && <VisualReviewPanel evidence={row} csrfToken={csrfToken} disabled={blocked} onAccessLost={onAccessLost} onBusy={onVisualBusy} />}
    </Paper>)}</Stack></Box>}
    {history?.ready_for_seal && !capture && !retry && <Alert severity="success">Current contents photo accepted. Insert the verified contents, seal the package, then press SEALED · CONTINUE.</Alert>}
    <Button size="small" variant="text" disabled={busy || retry} onClick={() => refresh().catch(fail)}>Refresh photo status</Button>
  </Stack></Paper>;
}
